import React from 'react';
import PropTypes from 'prop-types';
import BasicInfoStep from '../../steps/BasicInfoStep/BasicInfoStep';
import AssetSelectionStep from '../../steps/AssetSelectionStep/AssetSelectionStep';
import PhysicalConnectionStep from '../../steps/PhysicalConnectionStep/PhysicalConnectionStep';
import PreviewStep from '../../steps/PreviewStep/PreviewStep';
import './WizardStep.scss';

/**
 * WizardStep - Dynamic step renderer that displays the appropriate step component
 * Routes to specific step components based on step configuration
 */
const WizardStep = ({
    step,
    stepData = {},
    allStepData = {},
    onStepDataChange = () => { },
    onStepComplete = () => { },
    onValidationChange = () => { }, // Add validation callback
    isActive = false,
    mode = 'create',
    sites = [],
    actions = {},
    className = '',
    // Redux state for assets
    availableAssets = [],
    isLoadingAssets = false,
    assetsError = null,
    // Redux state for asset subtypes
    assetSubTypes = [],
    isLoadingAssetSubTypes = false,
    assetSubTypesError = null,
    // Redux state for asset details
    assetDetails = [],
    isLoadingAssetDetails = false,
    assetDetailsError = null,
    hierarchyData = null,
    organizationId = null,
    preSelectedAssetIds = []
}) => {
    const renderStepComponent = () => {
        if (!step) {
            return (
                <div className='step-error'>
                    <h3>Step Not Found</h3>
                    <p>The requested step could not be loaded.</p>
                </div>
            );
        }

        const commonProps = {
            stepData,
            onStepDataChange,
            onStepComplete,
            onValidationChange,
            mode,
            isActive
        };

        // Add allStepData for steps that need access to other step data
        const propsWithAllData = {
            ...commonProps,
            allStepData
        };

        switch (step.id) {
            case 'STEP1':
                return <BasicInfoStep {...commonProps} sites={sites} hierarchyData={hierarchyData} organizationId={organizationId} />;

            case 'STEP2':
                return <AssetSelectionStep
                    {...propsWithAllData}
                    actions={actions}
                    availableAssets={availableAssets}
                    isLoadingAssets={!!isLoadingAssets}
                    assetsError={assetsError}
                    assetSubTypes={assetSubTypes}
                    isLoadingAssetSubTypes={!!isLoadingAssetSubTypes}
                    assetSubTypesError={assetSubTypesError}
                    preSelectedAssetIds={preSelectedAssetIds}
                />;

            case 'STEP3':
                return <PhysicalConnectionStep
                    {...propsWithAllData}
                    actions={actions}
                    assetDetails={assetDetails}
                    isLoadingAssetDetails={!!isLoadingAssetDetails}
                    assetDetailsError={assetDetailsError}
                />;

            case 'STEP4':
                return <PreviewStep {...propsWithAllData} />;

            default:
                return (
                    <div className='step-error'>
                        <h3>Unknown Step</h3>
                        <p>Step &quot;{step.id}&quot; is not implemented.</p>
                        <div className='step-details'>
                            <strong>Step Configuration:</strong>
                            <pre>{JSON.stringify(step, null, 2)}</pre>
                        </div>
                    </div>
                );
        }
    };

    return (
        <div className={`wizard-step ${isActive ? 'active' : 'inactive'} ${className}`}>
            {/*<div className='step-header'>
                <h2 className='step-title'>{step?.title}</h2>
                {step?.description &&
                    <p className='step-description'>{step.description}</p>
                }
            </div>*/}
            <div className='step-body'>
                {renderStepComponent()}
            </div>
        </div>
    );
};

WizardStep.propTypes = {
    step: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        description: PropTypes.string,
        key: PropTypes.string,
        isRequired: PropTypes.bool,
        validationSchema: PropTypes.string
    }),
    stepData: PropTypes.object,
    allStepData: PropTypes.object,
    onStepDataChange: PropTypes.func,
    onStepComplete: PropTypes.func,
    onValidationChange: PropTypes.func,
    isActive: PropTypes.bool,
    mode: PropTypes.oneOf(['create', 'edit']),
    sites: PropTypes.array,
    actions: PropTypes.object,
    className: PropTypes.string,
    // Redux state for assets
    availableAssets: PropTypes.array,
    isLoadingAssets: PropTypes.bool,
    assetsError: PropTypes.object,
    // Redux state for asset subtypes
    assetSubTypes: PropTypes.array,
    isLoadingAssetSubTypes: PropTypes.bool,
    assetSubTypesError: PropTypes.object,
    // Redux state for asset details
    assetDetails: PropTypes.array,
    isLoadingAssetDetails: PropTypes.bool,
    assetDetailsError: PropTypes.object,
    hierarchyData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    organizationId: PropTypes.number,
    preSelectedAssetIds: PropTypes.array
};

export default WizardStep;
